import { useEffect, useRef, useState } from 'react';
import { Button, Flex, Form, message } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import CustomButton from '@/components/CustomButton';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import { CustomShipmentText } from '@/components';
import { RightTailArrowIcon } from '@/assets/icons/icons';
import { useSpin } from '@/components/SpinContext';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { SaveProductTimeOutOfRange } from '@/apis/settings';

const formatTemp = (value?: number | string | null, unit?: string): string =>
  value !== null && value !== undefined && value !== ''
    ? `${value}°${unit || 'C'}`
    : '—';

const MKT_VALIDATION_MESSAGE =
  'Only numbers allowed (up to 5 digits, minus allowed)';

const LABEL_RANGE_REQUIRED_MESSAGE = 'This field is required';

const LABEL_RANGE_ORDER_MESSAGE = 'Min value must be less than max value';

// Product temperature ranges (which label range defaults from) are entered as
// free text and commonly include decimals (e.g. 2.5°C), unlike MKT which is
// whole-number only — a whole-number-only pattern here would fail existing
// saved/defaulted values before the user even touches the field.
const LABEL_RANGE_VALIDATION_MESSAGE =
  'Only numbers allowed (up to 5 digits, decimal point and minus allowed)';

const LABEL_RANGE_PATTERN = /^-?\d{1,5}(\.\d{1,2})?$/;

const ACTIVATION_ENERGY_VALIDATION_MESSAGE =
  'Only numbers allowed (up to 10 digits, decimal point allowed)';

export interface UiDocument {
  id?: string;
  file?: File;
  name: string;
  setBy: string;
  setAt: string;
  url?: string;
}

interface RuleDocumentResponse {
  id?: string;
  name?: string;
  file_name?: string;
  filename?: string;
  original_name?: string;
  set_by?: string;
  setBy?: string;
  created_by?: string;
  createdBy?: string;
  uploaded_by?: string;
  set_at?: string;
  setAt?: string;
  created_at?: string;
  createdAt?: string;
  date?: string;
  url?: string;
}

export interface TimeOutOfRangeFormValues {
  mktMustNotExceed?: string;
  activationEnergy?: string;
  labelRangeMin?: string;
  labelRangeMax?: string;
}

export interface StagedTimeOutOfRangeData {
  values: TimeOutOfRangeFormValues;
  documents: UiDocument[];
  removedDocumentIds: string[];
}

export const buildTimeOutOfRangeFormData = (
  values: TimeOutOfRangeFormValues,
  documents: UiDocument[],
  removedDocumentIds: string[],
  isEnabled: boolean
): FormData => {
  const formData = new FormData();
  formData.append('is_enabled', String(isEnabled));

  if (values.mktMustNotExceed) {
    formData.append('mkt_limit_celsius', values.mktMustNotExceed);
  }
  if (values.activationEnergy) {
    formData.append('activation_energy_kj_mol', values.activationEnergy);
  }
  formData.append('allowable_range_above_min', '0');
  formData.append('allowable_range_below_min', '0');

  formData.append('label_range_min', values.labelRangeMin ?? '');
  formData.append('label_range_max', values.labelRangeMax ?? '');

  documents.forEach((doc) => {
    if (doc.file) {
      formData.append('files', doc.file);
    } else if (doc.id) {
      formData.append('existing_document_ids', doc.id);
    }
  });

  removedDocumentIds.forEach((docId) => {
    formData.append('remove_file_ids', docId);
  });

  return formData;
};

interface TimeOutOfRangeApiData {
  mkt_limit_celsius?: number | string | null;
  activation_energy_kj_mol?: number | string | null;
  label_range_min?: number | string | null;
  label_range_max?: number | string | null;
  files?: RuleDocumentResponse[];
  rule_documents?: RuleDocumentResponse[];
  documents?: RuleDocumentResponse[];
  rule_document?: RuleDocumentResponse;
}

interface TimeOutOfRangeDrawerProps {
  timeOutOfRangeData?: TimeOutOfRangeApiData;
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  productId?: string;
  token: string;
  temperatureMin?: number | string | null;
  temperatureMax?: number | string | null;
  temperatureUnit?: string;
  timeOutOfRangeEnabled: boolean;
  currentUserName?: string;
  unauthorize?: (error: { status?: number }) => void;
  onStageForCreate?: (data: StagedTimeOutOfRangeData) => void;
  stagedData?: StagedTimeOutOfRangeData | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const mapRuleDocument = (
  doc: RuleDocumentResponse,
  currentUserName: string | undefined,
  formatDateTime: (
    value?: string | null,
    options?: { includeTime?: boolean }
  ) => string
): UiDocument => ({
  id: doc.id,
  name: doc.name || 'Rule document',
  setBy: doc.created_by || currentUserName || 'You',
  setAt: formatDateTime(doc.created_at || doc.date, { includeTime: false }),
  url: doc.url,
});

const getRuleDocuments = (data: unknown): RuleDocumentResponse[] => {
  if (!isRecord(data)) return [];

  const files = data.files;
  if (Array.isArray(files)) return files as RuleDocumentResponse[];

  const ruleDocuments = data.rule_documents;
  if (Array.isArray(ruleDocuments)) {
    return ruleDocuments as RuleDocumentResponse[];
  }

  const documents = data.documents;
  if (Array.isArray(documents)) return documents as RuleDocumentResponse[];

  const ruleDocument = data.rule_document;
  if (isRecord(ruleDocument)) return [ruleDocument as RuleDocumentResponse];

  return [];
};

const formatDocumentMeta = (doc: UiDocument) =>
  `Set by ${doc.setBy}${doc.setAt ? ` \u00b7 ${doc.setAt}` : ''}`;

/**
 * "Time out of range" sub-drawer launched from the product form's Temperature
 * GxP row. Owns its own GET (on open) / PATCH (on save) calls (see
 * apis/settings.ts) when editing an existing product; in the add case
 * (no productId yet) Save stages the built FormData via onStageForCreate
 * instead, for the product form to send once the product is created.
 */
const TimeOutOfRangeDrawer: React.FC<TimeOutOfRangeDrawerProps> = ({
  timeOutOfRangeData,
  open,
  onClose,
  onSaveSuccess,
  productId,
  token,
  temperatureMin,
  temperatureMax,
  temperatureUnit,
  timeOutOfRangeEnabled,
  currentUserName,
  unauthorize,
  onStageForCreate,
  stagedData,
}) => {
  const [form] = Form.useForm<TimeOutOfRangeFormValues>();
  const { showSpin, hideSpin } = useSpin();
  const { formatDateTime } = useUserDateTimeFormat();
  const [documents, setDocuments] = useState<UiDocument[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Add case: there's no saved TOR data yet (nothing to fetch without a
    // product id), but the drawer still needs to open pre-filled with the
    // temperature range the user picked on the product form.
    if (!open) return;

    if (stagedData) {
      // Reopening after a previous stage — restore exactly what the user
      // had instead of re-deriving defaults from temperatureMin/Max
      form.setFieldsValue(stagedData.values);
      setDocuments(stagedData.documents);
      setRemovedDocumentIds(stagedData.removedDocumentIds);
      return;
    }

    setRemovedDocumentIds([]);

    form.setFieldsValue({
      mktMustNotExceed:
        timeOutOfRangeData?.mkt_limit_celsius != null
          ? String(timeOutOfRangeData.mkt_limit_celsius)
          : '',
      activationEnergy:
        timeOutOfRangeData?.activation_energy_kj_mol != null
          ? String(timeOutOfRangeData.activation_energy_kj_mol)
          : '',
      labelRangeMin:
        timeOutOfRangeData?.label_range_min != null &&
        timeOutOfRangeData.label_range_min !== ''
          ? String(timeOutOfRangeData.label_range_min)
          : temperatureMin != null && temperatureMin !== ''
            ? String(temperatureMin)
            : '',
      labelRangeMax:
        timeOutOfRangeData?.label_range_max != null &&
        timeOutOfRangeData.label_range_max !== ''
          ? String(timeOutOfRangeData.label_range_max)
          : temperatureMax != null && temperatureMax !== ''
            ? String(temperatureMax)
            : '',
    });

    const documentsList = getRuleDocuments(timeOutOfRangeData);

    setDocuments(
      documentsList.map((doc) =>
        mapRuleDocument(doc, currentUserName, formatDateTime)
      )
    );
  }, [
    open,
    stagedData,
    timeOutOfRangeData,
    currentUserName,
    form,
    temperatureMin,
    temperatureMax,
    formatDateTime,
  ]);

  // Label range is user-editable (defaults from the product's Temperature
  // range) — the summary bar and the "Allowable ranges above/below" sections
  // describe boundaries relative to it, so they need to track its live value.
  const watchedLabelRangeMin = Form.useWatch('labelRangeMin', form);
  const watchedLabelRangeMax = Form.useWatch('labelRangeMax', form);

  const minLabel = formatTemp(watchedLabelRangeMin, temperatureUnit);
  const maxLabel = formatTemp(watchedLabelRangeMax, temperatureUnit);

  const openFileDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const addDocumentsFromFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const pdfFiles = files.filter(
      (file) =>
        file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length !== files.length) {
      message.warning('Only PDF files can be uploaded');
    }

    const newDocuments: UiDocument[] = pdfFiles.map((file) => ({
      file,
      name: file.name,
      setBy: currentUserName || 'You',
      setAt: formatDateTime(new Date(), { includeTime: false }),
    }));
    setDocuments((prev) => [...prev, ...newDocuments]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    addDocumentsFromFiles(event.target.files);
    event.target.value = '';
  };

  const removeDocument = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setDocuments((prev) => {
      const doc = prev[index];
      if (doc?.id) {
        setRemovedDocumentIds((ids) => [...ids, doc.id!]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // MKT is signed, up-to-5-digit whole numbers only. Bound to a specific
  // field name so the inline error lands on the right Form.Item
  // (validateTrigger is disabled on this field — see note below on why the
  // sanitized value can't just rely on antd's own rules).
  const makeNumericFieldSanitizer =
    (fieldName: keyof TimeOutOfRangeFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const isNegative = raw.trim().startsWith('-');
      const digitsOnly = raw.replace(/[^0-9]/g, '');
      const hasInvalidInput = /[^0-9-]/.test(raw) || digitsOnly.length > 5;

      // The sanitized value below is always valid, so antd's own rule-based
      // validation would silently clear this error the moment it re-runs.
      // Setting it manually (with validateTrigger disabled on this field)
      // is what actually shows feedback while the user is typing.
      form.setFields([
        {
          name: fieldName,
          errors: hasInvalidInput ? [MKT_VALIDATION_MESSAGE] : [],
        },
      ]);

      const cappedDigits = digitsOnly.slice(0, 5);
      if (!cappedDigits) {
        // Allow a lone "-" through so the user can keep typing digits after it.
        return isNegative ? '-' : '';
      }
      return `${isNegative ? '-' : ''}${cappedDigits}`;
    };

  const sanitizeMktValue = makeNumericFieldSanitizer('mktMustNotExceed');

  // Label range defaults from the product's (free-text) temperature range,
  // which allows decimals and negative values — unlike MKT above.
  const makeLabelRangeSanitizer =
    (fieldName: 'labelRangeMin' | 'labelRangeMax') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const isNegative = raw.trim().startsWith('-');
      const unsigned = raw.replace(/^-/, '');
      const decimalCount = (unsigned.match(/\./g) || []).length;
      const intDigitsTyped = unsigned.split('.')[0].replace(/[^0-9]/g, '');
      const hasInvalidInput =
        /[^0-9.]/.test(unsigned) ||
        decimalCount > 1 ||
        intDigitsTyped.length > 5;

      form.setFields([
        {
          name: fieldName,
          errors: hasInvalidInput ? [LABEL_RANGE_VALIDATION_MESSAGE] : [],
        },
      ]);

      let sawDecimal = false;
      let intDigitCount = 0;
      let decimalDigitCount = 0;
      let sanitized = '';
      for (const char of unsigned) {
        if (char === '.' && !sawDecimal) {
          sawDecimal = true;
          sanitized += char;
        } else if (/[0-9]/.test(char)) {
          if (!sawDecimal && intDigitCount < 5) {
            intDigitCount += 1;
            sanitized += char;
          } else if (sawDecimal && decimalDigitCount < 2) {
            decimalDigitCount += 1;
            sanitized += char;
          }
        }
      }

      if (!sanitized) {
        // Allow a lone "-" through so the user can keep typing digits after it.
        return isNegative ? '-' : '';
      }
      return `${isNegative ? '-' : ''}${sanitized}`;
    };

  const sanitizeLabelRangeMinValue = makeLabelRangeSanitizer('labelRangeMin');
  const sanitizeLabelRangeMaxValue = makeLabelRangeSanitizer('labelRangeMax');

  const sanitizeActivationEnergyValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;
    const decimalCount = (raw.match(/\./g) || []).length;
    const digitCount = raw.replace(/[^0-9]/g, '').length;
    const hasInvalidInput =
      /[^0-9.]/.test(raw) || decimalCount > 1 || digitCount > 10;

    form.setFields([
      {
        name: 'activationEnergy',
        errors: hasInvalidInput ? [ACTIVATION_ENERGY_VALIDATION_MESSAGE] : [],
      },
    ]);

    // Keep digits and a single decimal point, capped at 10 digits total.
    let sawDecimal = false;
    let sanitizedDigitCount = 0;
    let sanitized = '';
    for (const char of raw) {
      if (char === '.' && !sawDecimal) {
        sawDecimal = true;
        sanitized += char;
      } else if (/[0-9]/.test(char) && sanitizedDigitCount < 10) {
        sanitizedDigitCount += 1;
        sanitized += char;
      }
    }

    return sanitized;
  };

  const handleSave = (values: TimeOutOfRangeFormValues) => {
    if (!productId) {
      onStageForCreate?.({ values, documents, removedDocumentIds });
      onClose();
      return;
    }

    const formData = buildTimeOutOfRangeFormData(
      values,
      documents,
      removedDocumentIds,
      timeOutOfRangeEnabled
    );

    showSpin();
    SaveProductTimeOutOfRange(token, productId, formData)
      .then((res) => {
        const resData = res?.data?.data;
        if (resData?.is_success) {
          message.success(
            resData?.message || 'Time out of range settings saved'
          );
          onSaveSuccess?.();
          onClose();
        } else {
          message.error(
            resData?.message || 'Failed to save time out of range settings'
          );
        }
      })
      .catch((error) => {
        unauthorize?.(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  return (
    <>
      <div className='drawer-sticky-header-section'>
        <div className='time-out-of-range-title-wrapper'>
          <CustomTitle
            text='Time out of range'
            fontSize={24}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>
      </div>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSave}
        className='scrollable-content'
      >
        <Flex vertical gap={24}>
          {/* Allowable range summary */}
          <div>
            <CustomShipmentText
              text='Allowable range summary'
              fontWeight={600}
              marginBottom='8px'
            />
            <Flex justify='space-between' className='mb-1'>
              <span className='product-range-graph-label time-out-of-range-summary-label'>
                0min
              </span>

              <span className='product-range-graph-label time-out-of-range-summary-label'>
                0min
              </span>
            </Flex>

            <Flex className='time-out-of-range-summary-bar'>
              <div className='time-out-of-range-bar-segment time-out-of-range-bar-segment--out' />
              <div className='time-out-of-range-bar-segment time-out-of-range-bar-segment--in' />
              <div className='time-out-of-range-bar-segment time-out-of-range-bar-segment--out' />
            </Flex>

            <Flex justify='space-between' className='mt-1 px-5'>
              <span className='product-range-graph-label'>{minLabel}</span>
              <span className='product-range-graph-label'>{maxLabel}</span>
            </Flex>
          </div>

          {/* Label range — editable min/max, defaults to the product's Temperature range */}
          <div>
            <Flex align='center' gap={2} className='mb-2'>
              <CustomShipmentText
                text='Label range'
                fontWeight={600}
                marginBottom='0px'
              />
              <span
                style={{ color: 'var(--danger)', fontSize: 14, lineHeight: 1 }}
              >
                *
              </span>
            </Flex>
            <Flex align='start' gap={6}>
              <Form.Item
                name='labelRangeMin'
                className='mb-0 flex-1'
                getValueFromEvent={sanitizeLabelRangeMinValue}
                validateTrigger={[]}
                dependencies={['labelRangeMax']}
                rules={[
                  {
                    required: true,
                    message: LABEL_RANGE_REQUIRED_MESSAGE,
                  },
                  {
                    pattern: LABEL_RANGE_PATTERN,
                    message: LABEL_RANGE_VALIDATION_MESSAGE,
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const maxValue = getFieldValue('labelRangeMax');
                      if (
                        !value ||
                        !maxValue ||
                        !LABEL_RANGE_PATTERN.test(value) ||
                        !LABEL_RANGE_PATTERN.test(maxValue)
                      ) {
                        return Promise.resolve();
                      }
                      return Number(value) >= Number(maxValue)
                        ? Promise.reject(new Error(LABEL_RANGE_ORDER_MESSAGE))
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                <CustomInput
                  placeholder='Min'
                  placeholderColor='var(--text-secondary)'
                  suffix={`°${temperatureUnit || 'C'}`}
                />
              </Form.Item>
              <Flex
                align='center'
                style={{ height: '36px' }}
                className='rotate-180'
              >
                <RightTailArrowIcon />
              </Flex>
              <Form.Item
                name='labelRangeMax'
                className='mb-0 flex-1'
                getValueFromEvent={sanitizeLabelRangeMaxValue}
                validateTrigger={[]}
                dependencies={['labelRangeMin']}
                rules={[
                  {
                    required: true,
                    message: LABEL_RANGE_REQUIRED_MESSAGE,
                  },
                  {
                    pattern: LABEL_RANGE_PATTERN,
                    message: LABEL_RANGE_VALIDATION_MESSAGE,
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const minValue = getFieldValue('labelRangeMin');
                      if (
                        !value ||
                        !minValue ||
                        !LABEL_RANGE_PATTERN.test(value) ||
                        !LABEL_RANGE_PATTERN.test(minValue)
                      ) {
                        return Promise.resolve();
                      }
                      return Number(value) <= Number(minValue)
                        ? Promise.reject(new Error(LABEL_RANGE_ORDER_MESSAGE))
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                <CustomInput
                  placeholder='Max'
                  placeholderColor='var(--text-secondary)'
                  suffix={`°${temperatureUnit || 'C'}`}
                />
              </Form.Item>
            </Flex>
          </div>

          {/* Allowable ranges above/below — not enabled for V1 */}
          <div>
            <CustomShipmentText
              text={`Allowable ranges above ${maxLabel}`}
              fontWeight={600}
              marginBottom='8px'
            />
            <Flex align='center' gap={6}>
              <Flex align='center' gap={6}>
                <CustomInput
                  value={`${maxLabel} and above`}
                  disabled
                  border='2px solid #E8E8E8'
                  bgColor='var(--surface-container)'
                  className='flex-1'
                />
                <CustomInput value='0' disabled suffix='min' width='43%' />
              </Flex>
              <Button
                disabled
                shape='circle'
                className='border-0 time-out-of-range-plus-btn'
                icon={<PlusOutlined />}
              />
            </Flex>
          </div>

          <div>
            <CustomShipmentText
              text={`Allowable ranges below ${minLabel}`}
              fontWeight={600}
              marginBottom='8px'
            />
            <Flex align='center' gap={6}>
              <Flex align='center' gap={6}>
                <CustomInput
                  value={`${minLabel} and below`}
                  disabled
                  border='2px solid #E8E8E8'
                  bgColor='var(--surface-container)'
                  className='flex-1'
                />
                <CustomInput value='0' disabled suffix='min' width='43%' />
              </Flex>
              <Button
                disabled
                shape='circle'
                icon={<PlusOutlined />}
                className='border-0 time-out-of-range-plus-btn'
              />
            </Flex>
          </div>

          {/* MKT must not exceed (optional) */}
          <div>
            <CustomShipmentText
              text='MKT must not exceed'
              fontWeight={600}
              marginBottom='8px'
            />
            <Form.Item
              name='mktMustNotExceed'
              className='mb-0'
              getValueFromEvent={sanitizeMktValue}
              validateTrigger={[]}
              rules={[
                {
                  pattern: /^-?\d{1,5}$/,
                  message: MKT_VALIDATION_MESSAGE,
                },
              ]}
            >
              <CustomInput
                placeholder='Enter value (optional)'
                placeholderColor='var(--text-secondary)'
                suffix={`°${temperatureUnit || 'C'}`}
              />
            </Form.Item>
          </div>

          {/* Activation energy (optional) */}
          <div>
            <CustomShipmentText
              text='Activation energy'
              fontWeight={600}
              marginBottom='8px'
            />
            <Form.Item
              name='activationEnergy'
              className='mb-0'
              getValueFromEvent={sanitizeActivationEnergyValue}
              validateTrigger={[]}
              rules={[
                {
                  pattern: /^\d{1,10}(\.\d{1,10})?$/,
                  message: ACTIVATION_ENERGY_VALIDATION_MESSAGE,
                },
              ]}
            >
              <CustomInput
                placeholder='Enter value (optional)'
                placeholderColor='var(--text-secondary)'
                suffix='kJ/mol'
              />
            </Form.Item>
          </div>

          {/* Upload rule document(s) */}
          <Flex vertical gap={8}>
            <CustomShipmentText text='Upload rule document' fontWeight={600} />
            <div
              onClick={openFileDialog}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                addDocumentsFromFiles(event.dataTransfer.files);
              }}
              className='time-out-of-range-dropzone'
            >
              <CustomShipmentText
                text='Drop files here to upload...'
                color='var(--text-light)'
                textAlign='center'
                marginBottom='10px'
              />
              <CustomButton
                text='Browse files'
                width='87px'
                height='28px'
                fontSize='12px'
                fontWeight='600'
                borderRadius='28px'
                bgColor='var(--light-gray)'
                padding='6px 8px'
                color='var(--text-primary)'
                boxShadow='none'
                onClick={openFileDialog}
              />
            </div>
            <input
              type='file'
              accept='application/pdf,.pdf'
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {documents.length > 0 && (
              <Flex vertical gap={8} className='mb-2'>
                {documents.map((doc, index) => (
                  <Flex
                    key={doc.id || `${doc.name}-${index}`}
                    align='center'
                    justify='space-between'
                    className='time-out-of-range-document-row'
                  >
                    <div>
                      {doc.url ? (
                        <a
                          href={doc.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ textDecoration: 'none' }}
                        >
                          <CustomShipmentText
                            text={doc.name}
                            fontSize='14px'
                            fontWeight='600'
                            color='var(--text-light)'
                            lineHeight='16px'
                          />
                        </a>
                      ) : (
                        <CustomShipmentText
                          text={doc.name}
                          fontSize='14px'
                          fontWeight='600'
                          color='var(--text-light)'
                          lineHeight='16px'
                        />
                      )}
                      <CustomShipmentText
                        text={formatDocumentMeta(doc)}
                        color='var(--text-light)'
                        lineHeight='20px'
                      />
                    </div>
                    <span
                      title='Remove'
                      className='no-bg cursor-pointer'
                      onClick={(event) => removeDocument(index, event)}
                    >
                      <CloseOutlined />
                    </span>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        </Flex>

        <div className='bottom-action-buttons'>
          <Flex vertical gap={8} className='mt-0'>
            <CustomButton
              text={'Save'}
              width={'100%'}
              height={48}
              borderRadius={'8px'}
              bgColor={'#000'}
              color={'#fff'}
              border='0px'
              fontSize={'16px'}
              fontWeight={600}
              htmlType='submit'
            />
            <CustomButton
              key='button1'
              width={'100%'}
              onClick={() => onClose()}
              text={'Cancel'}
              bgColor='transparent'
              color='black'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='48px'
            />
          </Flex>
        </div>
      </Form>
    </>
  );
};

export default TimeOutOfRangeDrawer;
